/**
 * Video Reviews — HLS-first playback for Shopify-hosted videos.
 * Uses native HLS when available (Safari), otherwise loads hls.js.
 */
(() => {
  if (window.__videoReviewsBooted) return;
  window.__videoReviewsBooted = true;

  const HLS_CDN = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.18/dist/hls.min.js';
  let hlsScriptPromise = null;

  function loadHlsLibrary() {
    if (window.Hls) return Promise.resolve(window.Hls);
    if (hlsScriptPromise) return hlsScriptPromise;

    hlsScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = HLS_CDN;
      script.async = true;
      script.onload = () => (window.Hls ? resolve(window.Hls) : reject(new Error('Hls missing')));
      script.onerror = () => reject(new Error('Failed to load hls.js'));
      document.head.appendChild(script);
    });

    return hlsScriptPromise;
  }

  function canPlayNativeHls(video) {
    return video.canPlayType('application/vnd.apple.mpegurl') !== '';
  }

  function pauseOtherReviews(current) {
    document.querySelectorAll('video-review-media[playing]').forEach((media) => {
      if (media !== current) media.pause();
    });
  }

  class VideoReviewMedia extends HTMLElement {
    #hls = null;
    #sourcesReady = null;
    #soundButton = null;
    #boundPlay = this.#onActivate.bind(this);
    #boundSoundToggle = this.#onSoundToggle.bind(this);
    #boundVolumeChange = this.#onVolumeChange.bind(this);

    connectedCallback() {
      const video = this.querySelector('video');
      if (!video) return;

      this.#soundButton = this.querySelector('[data-video-review-sound]');
      this.#soundButton?.addEventListener('click', this.#boundSoundToggle);
      video.addEventListener('volumechange', this.#boundVolumeChange);
      this.#syncSoundButton(video);

      this.#sourcesReady = this.#setupSources(video);

      if (this.hasAttribute('autoplay')) {
        this.#sourcesReady.then(() => this.#attemptAutoplay(video));
      } else {
        this.addEventListener('click', this.#boundPlay);
      }

      video.addEventListener('play', () => {
        pauseOtherReviews(this);
        this.setAttribute('playing', '');
        this.setAttribute('loaded', '');
        this.removeAttribute('suspended');
      });
      video.addEventListener('pause', () => {
        if (!video.seeking && video.paused) this.removeAttribute('playing');
      });
      video.addEventListener('ended', () => this.removeAttribute('playing'));
    }

    disconnectedCallback() {
      this.#destroyHls();
      this.removeEventListener('click', this.#boundPlay);
      this.#soundButton?.removeEventListener('click', this.#boundSoundToggle);
      this.querySelector('video')?.removeEventListener('volumechange', this.#boundVolumeChange);
    }

    async #setupSources(video) {
      const hlsSrc = this.dataset.hlsSrc;
      const mp4Src = this.dataset.mp4Src;

      if (!hlsSrc && !mp4Src) return;

      if (hlsSrc && canPlayNativeHls(video)) {
        video.src = hlsSrc;
        return;
      }

      if (hlsSrc) {
        try {
          const Hls = await loadHlsLibrary();
          if (Hls.isSupported()) {
            this.#hls = new Hls({
              enableWorker: true,
              lowLatencyMode: false,
              maxBufferLength: 30
            });
            this.#hls.loadSource(hlsSrc);
            this.#hls.attachMedia(video);
            return;
          }
        } catch (error) {
          console.warn('[VideoReviews] HLS unavailable, falling back to MP4', error);
        }
      }

      if (mp4Src) {
        video.src = mp4Src;
      }
    }

    #destroyHls() {
      if (this.#hls) {
        this.#hls.destroy();
        this.#hls = null;
      }
    }

    #attemptAutoplay(video) {
      video.muted = true;
      this.#syncSoundButton(video);
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          this.removeAttribute('playing');
          this.setAttribute('suspended', '');
          if (!this.hasAttribute('controls')) {
            video.controls = true;
          }
        });
      }
    }

    #onVolumeChange(event) {
      this.#syncSoundButton(event.currentTarget);
    }

    #syncSoundButton(video) {
      if (!this.#soundButton) return;

      const soundIsOn = !video.muted && video.volume > 0;
      this.#soundButton.dataset.muted = soundIsOn ? 'false' : 'true';
      this.#soundButton.setAttribute('aria-pressed', soundIsOn ? 'true' : 'false');
      this.#soundButton.setAttribute('aria-label', soundIsOn ? 'Mute video' : 'Play video with sound');
      this.toggleAttribute('sound-on', soundIsOn);
    }

    async #onSoundToggle(event) {
      event.preventDefault();
      event.stopPropagation();

      const video = this.querySelector('video');
      if (!video) return;

      await this.#sourcesReady;
      this.setAttribute('loaded', '');

      const shouldEnableSound = video.paused || video.muted || video.volume === 0;

      if (!shouldEnableSound) {
        video.muted = true;
        this.#syncSoundButton(video);
        return;
      }

      if (video.volume === 0) video.volume = 1;
      video.muted = false;
      this.#syncSoundButton(video);

      if (video.paused) {
        try {
          await video.play();
          this.setAttribute('playing', '');
          this.removeAttribute('suspended');
        } catch (error) {
          this.setAttribute('suspended', '');
          video.controls = true;
        }
      }
    }

    async #onActivate(event) {
      if (event.target.closest('a, [data-video-review-sound]')) return;

      const video = this.querySelector('video');
      if (!video) return;

      if (this.hasAttribute('playing')) {
        if (!this.hasAttribute('controls')) {
          video.pause();
        }
        return;
      }

      await this.#sourcesReady;
      this.setAttribute('loaded', '');

      try {
        await video.play();
        this.setAttribute('playing', '');
        this.removeAttribute('suspended');
      } catch (error) {
        this.setAttribute('suspended', '');
        video.controls = true;
      }
    }

    play() {
      return this.querySelector('video')?.play();
    }

    pause() {
      this.querySelector('video')?.pause();
    }
  }

  class VideoReviewsControls extends HTMLElement {
    #carousel = null;
    #progress = null;
    #resizeObserver = null;
    #frame = null;
    #boundUpdate = this.#scheduleUpdate.bind(this);

    connectedCallback() {
      this.#carousel = document.getElementById(this.getAttribute('aria-controls'));
      this.#progress = this.querySelector('[role="progressbar"]');

      if (!this.#carousel || !this.#progress) return;

      this.#carousel.addEventListener('scroll', this.#boundUpdate, { passive: true });
      this.#carousel.addEventListener('carousel:change', this.#boundUpdate);

      if ('ResizeObserver' in window) {
        this.#resizeObserver = new ResizeObserver(this.#boundUpdate);
        this.#resizeObserver.observe(this.#carousel);
      } else {
        window.addEventListener('resize', this.#boundUpdate);
      }

      this.#scheduleUpdate();
    }

    disconnectedCallback() {
      this.#carousel?.removeEventListener('scroll', this.#boundUpdate);
      this.#carousel?.removeEventListener('carousel:change', this.#boundUpdate);
      this.#resizeObserver?.disconnect();
      window.removeEventListener('resize', this.#boundUpdate);

      if (this.#frame) cancelAnimationFrame(this.#frame);
    }

    #scheduleUpdate() {
      if (this.#frame) return;

      this.#frame = requestAnimationFrame(() => {
        this.#frame = null;
        this.#updateProgress();
      });
    }

    #updateProgress() {
      if (!this.#carousel || !this.#progress) return;

      const scrollWidth = this.#carousel.scrollWidth;
      const visibleWidth = this.#carousel.clientWidth;
      const scrollPosition = Math.abs(this.#carousel.scrollLeft);
      const percentage = scrollWidth > 0
        ? Math.min(100, Math.max(0, ((scrollPosition + visibleWidth) / scrollWidth) * 100))
        : 0;

      this.style.setProperty('--video-reviews-progress', `${percentage}%`);
      this.#progress.setAttribute('aria-valuenow', Math.round(percentage).toString());
    }
  }

  if (!window.customElements.get('video-review-media')) {
    window.customElements.define('video-review-media', VideoReviewMedia);
  }

  if (!window.customElements.get('video-reviews-controls')) {
    window.customElements.define('video-reviews-controls', VideoReviewsControls);
  }
})();